// src/components/User/EditUserDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  updateUser,
  User,
  UpdateUserRequest,
  UserApiError,
} from '../../services/userApi';
import { getRoleDisplayName } from '../../utils/roleDisplayNames';

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User;
}

const USER_TYPES = [
  { value: 'TENANT', label: 'Tenant' },
  { value: 'FRAPORT', label: 'Fraport' },
  { value: 'CLIENT', label: 'Client' },
];

// Identity role names (value = internal; label = user-facing, Sprint J: Tenant User display)
const IDENTITY_ROLES = [
  { value: 'System Admin', label: 'System Admin' },
  { value: 'Exec Admin', label: 'Exec Admin' },
  { value: 'Airport Manager', label: 'Airport Manager' },
  { value: 'Fraport User', label: 'Fraport User' },
  { value: 'Client User', label: 'Client User' },
  { value: 'Tenant User (365 Tenant Sense)', label: getRoleDisplayName('Tenant User (365 Tenant Sense)') },
];

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [company, setCompany] = useState('');
  const [userType, setUserType] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Initialize form with user data
  useEffect(() => {
    if (user && open) {
      setEmail(user.email || '');
      setDisplayName(user.displayName || '');
      setCompany(user.company || '');
      setUserType(user.userType || '');
      // Use first role from roles array (Identity system), fallback to role field
      const userRole = (user.roles && user.roles.length > 0) ? user.roles[0] : (user.role || '');
      setRole(userRole);
      setError(null);
      setValidationErrors({});
    }
  }, [user, open]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Email validation
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = 'Invalid email address format';
      }
    }

    // Display name validation
    if (!displayName.trim()) {
      errors.displayName = 'Display name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const updateData: UpdateUserRequest = {
        email: email.trim(),
        displayName: displayName.trim(),
        company: company.trim() || null,
        userType: userType || null,
        roleName: role || null, // Backend expects 'roleName'
      };

      await updateUser(user.userId, updateData);
      
      onSuccess();
    } catch (err) {
      const apiError = err as UserApiError;
      console.error('EditUserDialog: Error updating user:', err);
      
      // Provide more helpful error messages
      let errorMessage = apiError.message || 'Failed to update user';
      
      if (apiError.code === 'NETWORK_ERROR') {
        // Network errors (connection reset, timeout, etc.)
        errorMessage = apiError.message || 'Network error: Unable to connect to the backend server. The connection was reset. This may indicate the backend server crashed or rejected the request. Please check the backend server logs.';
      } else if (apiError.code === 'NOT_FOUND') {
        errorMessage = `User not found (ID: ${user.userId}). The backend endpoint may not be implemented yet. Please verify the endpoint exists: PUT /api/admin/users/${user.userId}`;
      } else if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      } else if (apiError.code === 'VALIDATION_ERROR') {
        errorMessage = apiError.message || 'Validation failed. Please check your input.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setValidationErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationErrors({ ...validationErrors, email: '' });
            }}
            required
            fullWidth
            autoFocus
            disabled={isSubmitting}
            error={!!validationErrors.email}
            helperText={validationErrors.email || 'Email address of the user'}
          />

          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setValidationErrors({ ...validationErrors, displayName: '' });
            }}
            required
            fullWidth
            disabled={isSubmitting}
            error={!!validationErrors.displayName}
            helperText={validationErrors.displayName || 'Display name of the user'}
          />

          <TextField
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            fullWidth
            disabled={isSubmitting}
            helperText="Company name (optional)"
          />

          <FormControl fullWidth disabled={isSubmitting}>
            <InputLabel id="user-type-select-label" shrink={true}>
              User Type
            </InputLabel>
            <Select
              labelId="user-type-select-label"
              id="user-type-select"
              value={userType}
              label="User Type"
              onChange={(e) => setUserType(e.target.value)}
              displayEmpty
              renderValue={(selected: string) => {
                if (!selected) {
                  return <span style={{ color: '#999', fontStyle: 'italic' }}>Select user type (optional)</span>;
                }
                return USER_TYPES.find((t) => t.value === selected)?.label || selected;
              }}
            >
              <MenuItem value="">
                <em>None (optional)</em>
              </MenuItem>
              {USER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select the user type (optional)</FormHelperText>
          </FormControl>

          <FormControl fullWidth disabled={isSubmitting}>
            <InputLabel id="role-select-label" shrink={true}>
              Role
            </InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={role}
              label="Role"
              onChange={(e) => setRole(e.target.value)}
              displayEmpty
              renderValue={(selected: string) => {
                if (!selected) {
                  return <span style={{ color: '#999', fontStyle: 'italic' }}>Select role (optional)</span>;
                }
                return IDENTITY_ROLES.find((r) => r.value === selected)?.label || selected;
              }}
            >
              <MenuItem value="">
                <em>None (optional)</em>
              </MenuItem>
              {IDENTITY_ROLES.map((roleOption) => (
                <MenuItem key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select the role to assign to the user (optional)</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !email.trim() || !displayName.trim()}
        >
          {isSubmitting ? 'Updating...' : 'Update User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;

